import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import text from 'styles/utility/text.module.scss'
import { isBefore, isSameDay, eachDayOfInterval, startOfMonth, endOfMonth, addMonths } from 'date-fns'
import { BreadType } from 'types/bread'
import { Spacer } from 'components/Utility'
import { SmallHeadline, MainHeadline, SubDescription, CautionHeadline, Description } from 'components/Typography'
import { Card, ScheduleCardReadonly, ScheduleDetailCard } from 'components/Card'
import { CautionButton, GreyButton, NavButton, OrangeButton } from 'components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getSchedules } from 'reducks/schedule/selectors'
import { fetchSchedulesOfBread } from 'reducks/schedule/operations'
import { useSelectWeekday, useStringChangeEvent } from 'hooks/form'
import { ScheduleCalendarContainer } from 'components/Calendar'
import { getStocks } from 'reducks/stock/selectors'
import { addPreorderStocks, deletePreorderStocks, fetchFutureStocks } from 'reducks/stock/operations'
import { parseToDate, parseTimestampToDatetime, getBreadShortName } from 'lib/helper'
import { getClosedDates, getSpecialHolidays } from 'reducks/calendar/selectors'
import { getHolidays } from 'reducks/store/selectors'
import { fetchClosedDates } from 'reducks/calendar/operations'
import { BasicModal } from 'components/Modal'
import { FlexRow } from 'components/Flex'
import { ScheduleType } from 'types/schedule'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { getStoreRef } from 'reducks/auth/selectors'
import { fetchBreadOrdersInPeriod } from 'reducks/checkout/operations'
import { getOrders } from 'reducks/checkout/selectors'
import { CheckoutStatusType, OrderType } from 'types/checkout'
import { StoreCalendarType } from 'types/storeCalendar'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { useSetIndividualScheduleDates } from 'hooks/schedule'
import { StockType } from 'types/stock'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { ScheduleOption } from 'components/Schedule'
const daysOfWeekInstance = new DaysOfWeek()

type Props = {
  bread: BreadType
}

const ScheduleEditTemplate: FC<Props> = (props) => {
  const { bread } = props
  const type = 'preorder'
  const dispatch = useDispatch()
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const schedules: ScheduleType[] = useSelector((state: State) => getSchedules(state))
  const stocks: StockType[] = useSelector((state: State) => getStocks(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const orders: OrderType[] = useSelector((state: State) => getOrders(state))
  const storeCalendars: StoreCalendarType[] = useSelector((state: State) => getStoreCalendars(state))

  const [activeDates, setActiveDates] = useState<Date[]>([])
  const [businessDays, setBusinessDays] = useState<DayOfWeekType[]>([])
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [openCaution, setOpenCaution] = useState<boolean>(false)
  const [isEdited, setIsEdited] = useState<boolean>(false)
  const [individualSettingDates, setIndividualSettingDates] = useState<Date[]>([])
  const [preorderStartedAt, setPreorderStartedAt] = useState<string>('')
  const [hasScheduledPreorderStartedAt, setHasScheduledPreorderStartedAt] = useState<boolean>(false)
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeekType[]>([])

  const handleCurrentMonth = useCallback((month: Date) => setCurrentMonth(month), [setCurrentMonth])
  const handlePreorderStartedAt = useStringChangeEvent(setPreorderStartedAt, setIsEdited)
  const openCautionModal = useCallback(() => setOpenCaution(true), [setOpenCaution])
  const closeCautionModal = useCallback(() => setOpenCaution(false), [setOpenCaution])
  const selectDaysOfWeek = useSelectWeekday(setDaysOfWeek)
  const selectAllDaysOfWeek = useCallback(() => setDaysOfWeek([...businessDays]), [setDaysOfWeek, businessDays])
  const unselectAllDaysOfWeek = useCallback(() => setDaysOfWeek([]), [setDaysOfWeek])

  const selectDate = useCallback(
    (date: Date) => {
      // 過去の日付を追加しない、当日は追加できる
      if (isBefore(date, new Date()) && !isSameDay(date, new Date())) return

      setActiveDates((prevState) => {
        if (prevState.some((activeDate) => isSameDay(activeDate, date))) {
          return prevState.filter((activeDate) => !isSameDay(activeDate, date))
        } else {
          return [...prevState, date]
        }
      })

      setIndividualSettingDates((prevState) => {
        // 個別受付日を解除した場合
        if (prevState.some((individualSettingDate) => isSameDay(individualSettingDate, date))) {
          return prevState.filter((individualSettingDate) => !isSameDay(individualSettingDate, date))
        } else {
          return prevState
        }
      })
      setIsEdited(true)
    },
    [setActiveDates, setIsEdited]
  )

  // 選択した曜日で一括設定（上書き）する
  const selectAllDateByDaysOfWeek = useCallback(() => {
    // 当月の日付を全て取得する
    const datesInMonth = eachDayOfInterval({
      start: startOfMonth(currentMonth),
      end: endOfMonth(currentMonth),
    })

    const mergedDaysOfWeek = schedules.reduce((prev, schedule) => {
      return [...prev, ...schedule.days_of_week]
    }, [] as DayOfWeekType[])

    const dates: Date[] = []

    for (let i = 0; i < datesInMonth.length; i = (i + 1) | 0) {
      const date = datesInMonth[i]
      // 過去の日付であれば除外
      if (isBefore(date, new Date()) && !isSameDay(date, new Date())) continue
      // 休業日であれば除外
      if (closedDates.some((closedDate) => isSameDay(closedDate, date))) continue
      // 選択されていない曜日は除外
      if (!daysOfWeek.includes(daysOfWeekInstance.getDayOfWeek(date, specialHolidays))) continue
      dates.push(date)
    }

    // 当月の受付設定を一括更新
    setActiveDates((prevState) => {
      const activeDatesInOtherMonths = prevState.filter((activeDate) => {
        return datesInMonth.every((date) => !isSameDay(date, activeDate))
      })
      return [...activeDatesInOtherMonths, ...dates]
    })

    // 当月の個別設定を一括更新
    setIndividualSettingDates((prevState) => {
      // 他の月、休業日、受渡可能日以外の個別設定日を除外
      const remainingIndividualSettingDates = prevState.filter((individualSettingDate) => {
        return (
          datesInMonth.every((date) => !isSameDay(date, individualSettingDate)) ||
          closedDates.some((closedDate) => isSameDay(closedDate, individualSettingDate)) ||
          !mergedDaysOfWeek.includes(daysOfWeekInstance.getDayOfWeek(individualSettingDate, specialHolidays))
        )
      })
      // 選択されていない曜日だが注文のある日は個別設定日に追加
      const orderedDates = datesInMonth.filter(
        (date) =>
          !daysOfWeek.includes(daysOfWeekInstance.getDayOfWeek(date, specialHolidays)) &&
          orders.some((order) => isSameDay(date, parseToDate(order.date)))
      )
      return [...remainingIndividualSettingDates, ...orderedDates]
    })

    setDaysOfWeek([])
    setIsEdited(true)
  }, [
    daysOfWeek,
    closedDates,
    currentMonth,
    specialHolidays,
    orders,
    schedules,
    setActiveDates,
    setIndividualSettingDates,
    setDaysOfWeek,
    setIsEdited,
  ])

  // 事前予約商品のschedulesを取得
  useEffect(() => {
    if (!bread || !bread.bread_ref || !storeRef) return
    dispatch(fetchSchedulesOfBread(storeRef, bread.bread_ref, type))
  }, [bread, storeRef, type])

  // 事前予約商品のstocksを取得
  useEffect(() => {
    if (!bread || !bread.bread_ref || !storeRef) return
    dispatch(fetchFutureStocks({ storeRef, types: [type], breadRef: bread.bread_ref }))
  }, [bread, storeRef, type])

  // 事前予約の受渡可能日に設定済みの日付を取得
  useEffect(() => {
    const dates = stocks.map((stock) => parseToDate(stock.date))
    setActiveDates([...dates])
  }, [stocks])

  // 臨時休業日・営業日を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(new Date())))
  }, [storeRef])

  // 臨時休業日を考慮して選択月の翌月まで休業日を取得する
  useEffect(() => {
    if (!storeRef) return
    const startOfThisMonth = startOfMonth(new Date())
    dispatch(
      fetchClosedDates(
        startOfMonth(isBefore(startOfThisMonth, currentMonth) ? startOfThisMonth : currentMonth),
        endOfMonth(addMonths(currentMonth, 1)),
        holidays,
        specialHolidays,
        storeRef
      )
    )
  }, [currentMonth, holidays, storeRef, specialHolidays])

  // 注文情報を取得する
  useEffect(() => {
    if (!bread || !bread.bread_ref || !storeRef) return
    const status: CheckoutStatusType[] = ['succeeded', 'requires_capture']
    const start = startOfMonth(new Date())
    const end = endOfMonth(addMonths(currentMonth, 1))
    dispatch(fetchBreadOrdersInPeriod(start, end, storeRef, bread.bread_ref, status, 'preorder'))
  }, [currentMonth, bread, storeRef])

  // 事前予約の受付設定をローカルの値に反映する
  useEffect(() => {
    // 営業曜日を取得する
    const _businessDays = schedules.reduce((prev, schedule) => {
      return Array.from(new Set([...prev, ...schedule.days_of_week]))
    }, [] as DayOfWeekType[])
    setBusinessDays([..._businessDays])
    // 予約注文開始日の値を取得する
    const schedule = schedules[0]
    if (schedule && schedule.preorder_started_at) {
      setPreorderStartedAt(parseTimestampToDatetime(schedule.preorder_started_at))
      setHasScheduledPreorderStartedAt(true)
    } else {
      setPreorderStartedAt('')
    }
  }, [schedules])

  // 個別で設定されている日付かを判定
  useSetIndividualScheduleDates(schedules, setIndividualSettingDates, specialHolidays, stocks, storeCalendars)

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <SmallHeadline label={'対象の商品'} />
      <Spacer size={8} />
      <Card>
        <ScheduleCardReadonly
          breadName={getBreadShortName(bread)}
          imageUrl={bread.image_urls[0] || ''}
          hasStocks={stocks.length > 0}
        />
      </Card>
      <Spacer size={48} />
      <MainHeadline headline={'受渡日の登録'} />
      <Spacer size={28} />
      <SmallHeadline label={'受渡可能日'} required={true} />
      <Spacer size={8} />
      <SubDescription
        description={
          'お客さまに受け渡しできる日をタップして選択してください。sacriの定休日は、グレーで設定されています。'
        }
      />
      <Spacer size={12} />
      <ScheduleCalendarContainer
        activeDates={activeDates}
        businessDays={businessDays}
        currentMonth={currentMonth}
        daysOfWeek={daysOfWeek}
        handleCurrentMonth={handleCurrentMonth}
        individualSettingDates={individualSettingDates}
        selectAllDateByDaysOfWeek={selectAllDateByDaysOfWeek}
        selectAllDaysOfWeek={selectAllDaysOfWeek}
        selectDate={selectDate}
        selectDaysOfWeek={selectDaysOfWeek}
        unselectAllDaysOfWeek={unselectAllDaysOfWeek}
      />
      <Spacer size={20} />
      <ScheduleOption
        handlePreorderStartedAt={handlePreorderStartedAt}
        hasScheduledPreorderStartedAt={hasScheduledPreorderStartedAt}
        preorderStartedAt={preorderStartedAt}
      />
      <Spacer size={48} />
      <OrangeButton
        disabled={!isEdited || activeDates.length === 0}
        label={'設定を保存する'}
        onClick={() =>
          dispatch(
            addPreorderStocks(
              activeDates,
              bread,
              orders,
              stocks,
              schedules,
              individualSettingDates,
              preorderStartedAt,
              type
            )
          )
        }
        size={'medium'}
        type={'main'}
      />
      {stocks.length > 0 && (
        <>
          <Spacer size={12} />
          <NavButton
            label={'事前予約日設定を削除する'}
            iconPath={'/icon/grey/delete.svg'}
            onClick={openCautionModal}
            url={''}
          />
          <BasicModal open={openCaution} closeModal={closeCautionModal}>
            <CautionHeadline caution={'受付設定を削除します'} />
            <Spacer size={12} />
            <Description description={'一度削除した設定は、取り消すことが出来ませんがよろしいですか？'} />
            <p className={text.color_caution}>※すでにお客さまから受けている注文は、取り消すことはできません</p>
            <Spacer size={16} />
            <FlexRow justifyContent={'center'}>
              <GreyButton label={'キャンセル'} onClick={closeCautionModal} size={'small'} type={'outlined'} />
              <Spacer size={16} />
              <CautionButton
                label={'削除する'}
                onClick={() => dispatch(deletePreorderStocks(bread, schedules, stocks, orders))}
                size={'small'}
              />
            </FlexRow>
          </BasicModal>
        </>
      )}
      <Spacer size={48} />
      <MainHeadline headline={'商品設定情報'} />
      <Spacer size={28} />
      <ScheduleDetailCard schedules={schedules} />
      <Spacer size={48} />
      <NavButton label={'事前予約日設定に戻る'} iconPath={'/icon/grey/arrow_prev.svg'} url={'/schedule'} />
      <Spacer size={12} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default ScheduleEditTemplate
